import moment from 'moment';
import queryString from 'query-string';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useAwaitControl } from 'react-redux-await-control';
import { useHistory } from 'react-router';
import {
  clearOAuthState,
  EXTERNAL_LOGIN_START_PARAM,
  getExternalLoginStartUrl,
  getGoogleOAuthUrl,
  GoogleOAuthContext,
  OAuthHandshake,
  readOAuthHandshake,
  REDIRECT_LOGIN,
} from '~/common/utils/googleOauth';
import { getSafeRedirectPath } from '~/common/utils/safeRedirect';
import useIsAuthenticated, { TOKEN_KEY } from '~/hooks/useIsAuthenticated';
import { googleLogin, login } from '~/store/user/actions';
import { Button, Form, Input } from '~/ui/components';

import { FormContainer, FormMessage, ActionContainer, OrText, Heading } from './Login.styled';

type LoginMethod = 'both' | 'basic' | 'google';

const LOGIN_METHOD: LoginMethod = process.env.REACT_APP_LOGIN_METHOD as LoginMethod;

const LOGIN_MESSAGE = {
  basic: 'Para acessar o Backoffice, entre com seu email e senha',
  google: 'Para acessar o Backoffice, é necessário utilizar sua conta do Google clicando no botão abaixo',
  both: 'Para acessar o Backoffice, entre com seu email e senha ou utilize a sua conta do Google',
};

/** Calcula um valor uma única vez, na primeira renderização, e o mantém pelo ciclo de vida do componente. */
function useMountValue<T>(compute: () => T): T {
  const ref = useRef<{ value: T }>();
  if (!ref.current) ref.current = { value: compute() };
  return ref.current.value;
}

export function LoginForm(): ReactElement {
  const [loading, setLoading] = useState(false);
  const [showRedirectMessage, setShowRedirectMessage] = useState(false);
  // Falha ao montar a URL do Google (ex.: sessionStorage bloqueada): cai na tela de erro em vez
  // de ficar presa em "Só um momento…".
  const [oauthStartFailed, setOauthStartFailed] = useState(false);
  const { isAuthenticated, isExpired } = useIsAuthenticated();

  const history = useHistory();
  const { search } = history.location;
  const searchParams = queryString.parse(search);

  const googleLoginControl = useAwaitControl(googleLogin);
  const googleLoginLoading = googleLoginControl.isRunning();
  const googleLoginFailure = googleLoginControl.hasFailure();
  const googleLoginSuccess = googleLoginControl.isSuccessful();

  const loginControl = useAwaitControl(login);
  const loginSuccess = loginControl.isSuccessful();
  const loginFailure = loginControl.hasFailure();
  const loginLoading = loginControl.isRunning();

  const isExternalLoginStart = searchParams?.[EXTERNAL_LOGIN_START_PARAM] === 'external_login';

  // O callback do Google (code + state) e lido UMA vez: a URL e limpa logo em seguida para o code
  // nao ficar em historico, Referer ou logs (CWE-598), entao nada pode depender de searchParams.code.
  // Precisa ser snapshot: o clearOAuthState() do efeito abaixo apaga a sessionStorage, entao uma
  // releitura em render posterior voltaria null.
  const oauthCode = useMountValue<string | null>(() =>
    typeof searchParams?.code === 'string' && searchParams.code ? searchParams.code : null,
  );

  // Handshake do login resolvido uma única vez pelo nonce do state (null = state ausente ou inválido).
  const oauthHandshake = useMountValue<OAuthHandshake | null>(() =>
    oauthCode ? readOAuthHandshake(searchParams.state) : null,
  );
  const state: GoogleOAuthContext = oauthHandshake?.context ?? {};
  const invalidOAuthState = Boolean(oauthCode) && !oauthHandshake;

  const isInIframe = useMemo(() => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }, [window]);

  const isLoading = useMemo(
    () => loading || loginLoading || googleLoginLoading,
    [loading, loginLoading, googleLoginLoading],
  );

  const afterReceiveMessage = (event) => {
    if (event.origin !== window.location.origin) return;

    if (event.data.type === 'OAUTH_DONE') {
      localStorage.setItem('authenticationToken', event.data?.token);
      localStorage.setItem('authMethod', 'google');
      window.location.reload();
    }
  };

  const redirectAfterLogin = () => {
    const redirect = getSafeRedirectPath(localStorage.getItem('redirectTo'));
    const logoutTime = localStorage.getItem('logoutTime');
    const logoutTimeLimit = moment().diff(logoutTime, 'minutes') < 10;
    const redirectFromQuery = getSafeRedirectPath(searchParams?.redirectTo);

    if (redirectFromQuery) {
      return history.push(redirectFromQuery);
    }

    if (redirect && logoutTime && logoutTimeLimit) {
      localStorage.removeItem('redirectTo');
      localStorage.removeItem('logoutTime');
      return history.push(redirect);
    }

    return history.push('/backoffice/people');
  };

  const handleGoogleLogin = async () => {
    setLoading(true);

    const redirectTo = getSafeRedirectPath(searchParams?.redirectTo);
    if (redirectTo) {
      localStorage.setItem('redirectTo', redirectTo);
      localStorage.setItem('logoutTime', moment().format());
    }

    if (isInIframe) {
      setShowRedirectMessage(true);
      setLoading(false);
      window.open(getExternalLoginStartUrl(), 'oAuthBlankWindow');
      return;
    }

    try {
      window.location.href = await getGoogleOAuthUrl();
    } catch (error) {
      setLoading(false);
      setOauthStartFailed(true);
    }
  };

  const handleRetry = () => {
    window.location.replace('/login');
  };

  const handleBack = () => {
    setShowRedirectMessage(false);
    setLoading(false);
  };

  const handleOnSubmit = ({ username, password }): void => {
    setLoading(true);
    loginControl.start({ username, password });
  };

  const handleClose = () => {
    window.close();
  };

  useEffect(() => {
    if (isExternalLoginStart) {
      const context: GoogleOAuthContext = { action: 'external_login', keepToken: searchParams?.keepToken === 'true' };
      getGoogleOAuthUrl({ context })
        .then((url) => window.location.replace(url))
        .catch(() => setOauthStartFailed(true));
    }
  }, []);

  useEffect(() => {
    if (!oauthCode) return;

    // O nonce é de uso único: descartado assim que o callback é processado, válido ou não.
    clearOAuthState();

    // Tira code e state da URL antes de qualquer chamada: o code so vive na memoria deste componente.
    history.replace('/login');

    if (oauthHandshake) {
      googleLoginControl.start({
        token: oauthCode,
        redirectUri: REDIRECT_LOGIN,
        keepToken: oauthHandshake.context.keepToken,
        codeVerifier: oauthHandshake.codeVerifier,
      });
    }
  }, [oauthCode]);

  useEffect(() => {
    if (state?.action !== 'external_login' && (loginSuccess || googleLoginSuccess)) {
      redirectAfterLogin();
    }
  }, [loginSuccess, googleLoginSuccess]);

  useEffect(() => {
    if (loginFailure) {
      setLoading(false);
    }
  }, [loginFailure]);

  useEffect(() => {
    if (state?.action === 'external_login' && googleLoginSuccess) {
      const newToken = localStorage.getItem(TOKEN_KEY);
      window.opener?.postMessage({ type: 'OAUTH_DONE', token: newToken }, window.location.origin);
    }
  }, [googleLoginSuccess, searchParams]);

  useEffect(() => {
    window.addEventListener('message', afterReceiveMessage);

    return () => {
      window.removeEventListener('message', afterReceiveMessage);
    };
  }, []);

  if (isExternalLoginStart && !oauthStartFailed) {
    return (
      <FormContainer>
        <Heading>Só um momento…</Heading>
        <FormMessage>Estamos redirecionando você para o login com o Google.</FormMessage>
      </FormContainer>
    );
  }

  if (isAuthenticated && !isExpired && state?.action !== 'external_login' && !oauthStartFailed) {
    redirectAfterLogin();
    return <></>;
  }

  if (googleLoginFailure || invalidOAuthState || oauthStartFailed) {
    return (
      <FormContainer>
        <Heading>Ocorreu um erro!</Heading>

        <FormMessage>
          Não foi possível autenticar sua conta com o Google. Por favor, tente novamente ou entre em contato com o time
          de tecnologia.
        </FormMessage>
        <Button rounded fullWidth color="black" onClick={handleRetry}>
          Tentar novamente
        </Button>
      </FormContainer>
    );
  }

  if (state?.action === 'external_login' && googleLoginSuccess) {
    return (
      <FormContainer>
        <Heading>Tudo certo!</Heading>
        <FormMessage>
          A autenticação foi realizada com sucesso. Feche esta janela e volte para a tela da aplicação para continuar.
        </FormMessage>

        <Button variant="outline" rounded fullWidth color="black" disabled={loading} onClick={handleClose}>
          Fechar
        </Button>
      </FormContainer>
    );
  }

  if (showRedirectMessage) {
    return (
      <FormContainer>
        <Heading>Atenção!</Heading>
        <FormMessage>Para sua segurança, o login precisa ser realizado em uma nova janela do navegador.</FormMessage>

        <FormMessage>Clique em &quot;Voltar&quot; para retornar para a tela de login.</FormMessage>

        <Button variant="outline" rounded fullWidth color="black" disabled={loading} onClick={handleBack}>
          Voltar
        </Button>
      </FormContainer>
    );
  }

  if (oauthCode) {
    return (
      <FormContainer>
        <Heading>Só um momento…</Heading>

        <FormMessage>Estamos validando suas informações e em instantes você será redirecionado.</FormMessage>
        <FormMessage>Por favor, aguarde.</FormMessage>
      </FormContainer>
    );
  }

  return (
    <>
      <Heading>Acesse sua conta</Heading>

      <FormContainer>
        <FormMessage>{LOGIN_MESSAGE[LOGIN_METHOD]}</FormMessage>

        {['basic', 'both'].includes(LOGIN_METHOD) && (
          <Form
            onSubmit={handleOnSubmit}
            loading={isLoading ? 1 : 0}
            inputs={[
              {
                id: 'username',
                label: 'Usuário',
                input: <Input placeholder="Usuário" />,
              },
              {
                id: 'password',
                label: 'Senha',
                input: <Input password placeholder="Senha" />,
              },
            ]}
          >
            <Button title="Entrar" disabled={isLoading} rounded fullWidth type="submit" />
          </Form>
        )}

        <ActionContainer>
          {LOGIN_METHOD === 'both' && <OrText>Ou acesse com</OrText>}

          {['google', 'both'].includes(LOGIN_METHOD) && (
            <Button
              className="google-button"
              icon="Google"
              iconPosition="right"
              disabled={isLoading}
              fullWidth
              color="black"
              rounded
              onClick={handleGoogleLogin}
              type="button"
            >
              Entre com sua conta
            </Button>
          )}

          {/* {['basic', 'both'].includes(LOGIN_METHOD) && ( */}
          {/*  <> */}
          {/*    <Divider /> */}

          {/*    <AccessInfo> */}
          {/*      <AccessText>Ainda não tem acesso?</AccessText> */}
          {/*      <AccessLink href="/solicitar-acesso"> */}
          {/*        Solicite um acesso à sua liderança <ArrowRight className="fill-brand-primary" /> */}
          {/*      </AccessLink> */}
          {/*    </AccessInfo> */}
          {/*  </> */}
          {/* )} */}
        </ActionContainer>
      </FormContainer>
    </>
  );
}
